import * as yup from "yup";
import {
  emailMustBeValidEmail,
  fieldMaxLength,
  fieldMaxNumber,
  requiredField,
} from "../../../../../common/infrastructure/helpers/yupHelper";
import { UserRole } from "../../../../../common/enums";
import { Default_Instructor_Color } from "../../../../../common/infrastructure/utilities/colorUtility";

export type IEmployeeStudioFormModel = {
  studioId: number;
  studioName: string;
  // Group classes.
  commissionPercent?: number | null;
  weekendCommissionPercent?: number | null;
  // Individual classes.
  individualCommissionPercent?: number | null;
  individualWeekendCommissionPercent?: number | null;
  // MultiSport-paid items.
  multisportCommissionPercent?: number | null;
  multisportWeekendCommissionPercent?: number | null;
  // CoolFit-paid items.
  coolFitCommissionPercent?: number | null;
  coolFitWeekendCommissionPercent?: number | null;
  // Services.
  serviceCommissionPercent?: number | null;
  serviceWeekendCommissionPercent?: number | null;
  // Sold subscription plans.
  planCommissionPercent?: number | null;
  planWeekendCommissionPercent?: number | null;
};

// Reusable schema for a nullable commission-percent field (accepts decimals, max 100).
const commissionField = (label: string) =>
  yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null ? null : value,
    )
    .nullable()
    .min(0, fieldMaxNumber(label, 100))
    .max(100, fieldMaxNumber(label, 100))
    .defined();

const employeeStudioSchema = yup.object({
  studioId: yup.number().required().defined(),
  studioName: yup.string().defined(),
  commissionPercent: commissionField("Комисионен процент - Делник"),
  weekendCommissionPercent: commissionField("Комисионен процент - Уикенд"),
  individualCommissionPercent: commissionField(
    "Индивидуален комисионен процент - Делник",
  ),
  individualWeekendCommissionPercent: commissionField(
    "Индивидуален комисионен процент - Уикенд",
  ),
  multisportCommissionPercent: commissionField(
    "MultiSport комисионен процент - Делник",
  ),
  multisportWeekendCommissionPercent: commissionField(
    "MultiSport комисионен процент - Уикенд",
  ),
  coolFitCommissionPercent: commissionField(
    "CoolFit комисионен процент - Делник",
  ),
  coolFitWeekendCommissionPercent: commissionField(
    "CoolFit комисионен процент - Уикенд",
  ),
  serviceCommissionPercent: commissionField(
    "Комисионен процент за услуги - Делник",
  ),
  serviceWeekendCommissionPercent: commissionField(
    "Комисионен процент за услуги - Уикенд",
  ),
  planCommissionPercent: commissionField(
    "Комисионен процент за планове - Делник",
  ),
  planWeekendCommissionPercent: commissionField(
    "Комисионен процент за планове - Уикенд",
  ),
});

export const employeeWizardFormSchema = yup
  .object({
    id: yup.number().required().defined(),

    firstName: yup
      .string()
      .required(requiredField("Име"))
      .trim()
      .max(200, fieldMaxLength("Име", 200))
      .defined(),

    lastName: yup
      .string()
      .required(requiredField("Фамилия"))
      .trim()
      .max(200, fieldMaxLength("Фамилия", 200))
      .defined(),

    email: yup
      .string()
      .email(emailMustBeValidEmail())
      .required(requiredField("Имейл"))
      .trim()
      .max(200, fieldMaxLength("Имейл", 200))
      .defined(),

    phoneNumber: yup
      .string()
      .required(requiredField("Телефонен номер"))
      .trim()
      .max(200, fieldMaxLength("Телефонен номер", 200))
      .defined(),

    role: yup
      .mixed<UserRole>()
      .oneOf(Object.values(UserRole) as UserRole[])
      .required(requiredField("Роля"))
      .defined(),

    color: yup
      .string()
      .matches(/^[A-Fa-f0-9]+$/, "Невалиден цвят")
      .required(requiredField("Цвят"))
      .defined(),

    viewOnlyOwnEvents: yup.boolean().defined(),

    lockOthersEvents: yup.boolean().defined(),

    studios: yup
      .array()
      .of(employeeStudioSchema)
      .min(1, "Трябва да изберете поне един обект")
      .required()
      .defined(),
  })
  .required();

export type IEmployeeWizardFormModel = yup.InferType<
  typeof employeeWizardFormSchema
>;

export const employeeWizardFormDefaultValues: IEmployeeWizardFormModel = {
  id: 0,
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  role: UserRole.Employee_Instructor,
  color: Default_Instructor_Color,
  viewOnlyOwnEvents: false,
  lockOthersEvents: false,
  studios: [],
};
